import { useCallback, useEffect, useId, useRef, useSyncExternalStore, type KeyboardEvent, type RefObject } from 'react';

import { trapTabKey } from './focusTrap';
import {
  getOverlayStack,
  pushOverlayLayer,
  removeOverlayLayer,
  subscribeOverlayStack,
  type OverlayLayer,
} from './overlayStack';
import type { OverlayScope } from './scope';

/**
 * 浮层控制器。
 *
 * 一个浮层实例在「打开」期间做四件事，全部在这里：
 *   1. 把自己登记进全局堆栈（顺序、层级、谁是顶层）；
 *   2. 记下"打开时谁有焦点"，关闭时还回去；
 *   3. 打开后把焦点放进浮层（优先调用方指定的位置）；
 *   4. 点击外部请求关闭 —— 只对允许这件事的作用域生效。
 *
 * Esc 与滚动锁定**不在这里**，它们在堆栈里：前者必须全局只有一个处理器
 *（否则多层会一起响应），后者是全局计数（多层时只有最后一个关闭才解锁）。
 */

export function useOverlayStack(): readonly OverlayLayer[] {
  return useSyncExternalStore(subscribeOverlayStack, getOverlayStack, getOverlayStack);
}

export interface OverlayEntryOptions {
  open: boolean;
  scope: OverlayScope;
  label: string;
  onClose: () => void;
  panelRef: RefObject<HTMLElement | null>;
  initialFocus: RefObject<HTMLElement | null> | undefined;
  trapFocus: boolean;
  dismissOnOutsidePointer: boolean;
}

export interface OverlayEntryBinding {
  /** 在堆栈里的位置：0 是最底层；未打开时为 -1 */
  readonly depth: number;
  readonly isTop: boolean;
  /** 交给面板容器，用来做 Tab 循环 */
  readonly onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

export function useOverlayEntry(options: OverlayEntryOptions): OverlayEntryBinding {
  const { open, scope, label, panelRef, trapFocus, dismissOnOutsidePointer } = options;
  const id = useId();
  const stack = useOverlayStack();

  /**
   * 最新回调放进 ref，注册 effect 的依赖里只留"影响堆栈形态"的值。
   * 否则调用方每次渲染给一个新函数就会导致反复注销/重注册 —— 表现为
   * Esc 时有时无、焦点被反复抢回触发元素。这类 bug 只在真实交互里出现，测试很难抓到。
   */
  const latest = useRef<Pick<OverlayEntryOptions, 'onClose' | 'initialFocus'>>({
    onClose: options.onClose,
    initialFocus: options.initialFocus,
  });
  useEffect(() => {
    latest.current = { onClose: options.onClose, initialFocus: options.initialFocus };
  });

  useEffect(() => {
    if (!open) return;
    const active = typeof document === 'undefined' ? null : document.activeElement;
    const restoreFocus = active instanceof HTMLElement ? active : null;
    pushOverlayLayer({ id, scope, label, restoreFocus, close: () => latest.current.onClose() });

    return () => {
      removeOverlayLayer(id);
      // 焦点还给"打开这层时正在聚焦的元素"，也就是触发元素。
      // 必须先查 isConnected：多层同时关闭时它可能已经被一起卸掉，
      // 对脱离文档的节点调用 focus() 会被浏览器转成 body，焦点就丢了。
      if (restoreFocus !== null && restoreFocus.isConnected) restoreFocus.focus();
    };
  }, [id, open, scope, label]);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (panel === null) return;
    const preferred = latest.current.initialFocus?.current ?? null;
    (preferred ?? panel).focus();
  }, [id, open, panelRef]);

  useEffect(() => {
    if (!open || !dismissOnOutsidePointer) return;
    const handlePointerDown = (event: Event): void => {
      const panel = panelRef.current;
      const target = event.target;
      if (panel === null || target === null || !(target instanceof Node)) return;
      if (panel.contains(target)) return;
      latest.current.onClose();
    };
    // 捕获阶段：底下的控件可能自己 stopPropagation，但那不该让"点了外面"这件事失效
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [dismissOnOutsidePointer, open, panelRef]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>): void => {
      if (!trapFocus || event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (panel === null) return;
      trapTabKey(panel, event);
    },
    [panelRef, trapFocus],
  );

  const depth = stack.findIndex((layer) => layer.id === id);
  return {
    depth,
    isTop: depth !== -1 && depth === stack.length - 1,
    onKeyDown,
  };
}
